<template>
  <div class="tour">
    <!-- ── Header ─────────────────────────────────────────────────── -->
    <header class="tour-hero">
      <div class="tour-hero-text">
        <router-link to="/distribution-notes" class="tour-back">
          <i class="ph ph-arrow-left" aria-hidden="true"></i> Bons distribution
        </router-link>
        <span class="dash-eyebrow">Tournée de livraison</span>
        <h1 class="tour-title">{{ note.note_ref || refNote }}</h1>
        <p class="tour-sub">
          <template v-if="note.zone_name">{{ note.zone_name }}</template>
          <template v-if="note.note_city"> · {{ note.note_city }}</template>
          <template v-if="note.created_at"> · {{ getDate(note.created_at) }}</template>
        </p>
      </div>

      <div class="tour-progress" :aria-label="`${doneCount} sur ${parcels.length} colis traités`">
        <div class="tour-progress-top">
          <b>{{ doneCount }}</b><span>/ {{ parcels.length }} traités</span>
        </div>
        <div class="tour-progress-bar"><span :style="{ width: progressPct + '%' }"></span></div>
        <small>{{ todoCount }} à livrer · {{ money(todoCash) }} {{ $devis }} à encaisser</small>
      </div>
    </header>

    <!-- ── Location / geocoding status ────────────────────────────── -->
    <div class="tour-locate" :class="`is-${geoState}`">
      <span class="tour-locate-icon">
        <i
          :class="geoState === 'ok' ? 'ph ph-navigation-arrow' : geoState === 'pending' ? 'ph ph-crosshair' : 'ph ph-map-pin-line'"
          aria-hidden="true"
        ></i>
      </span>
      <div class="tour-locate-text">
        <b v-if="geoState === 'ok'">Trié du plus proche au plus loin depuis votre position</b>
        <b v-else-if="geoState === 'pending'">Localisation en cours…</b>
        <b v-else>Position indisponible : tri par ville et quartier</b>
        <span v-if="geocoding">Localisation des quartiers {{ geoDone }}/{{ geoTotal }}…</span>
        <span v-else-if="geoState === 'denied'">Autorisez la localisation dans le navigateur pour trier par proximité.</span>
        <span v-else-if="unlocatedStops">{{ unlocatedStops }} arrêt(s) non localisé(s), placés en fin de liste.</span>
      </div>
      <button type="button" class="btn btn-primary tour-locate-btn" :disabled="geoState === 'pending'" @click="locate">
        <i class="ph ph-arrows-clockwise" aria-hidden="true"></i>
        <span>Actualiser ma position</span>
      </button>
    </div>
    <div v-if="geocoding" class="tour-geo-bar" aria-hidden="true">
      <span :style="{ width: (geoTotal ? (geoDone / geoTotal) * 100 : 0) + '%' }"></span>
    </div>

    <!-- ── Toolbar ────────────────────────────────────────────────── -->
    <div class="tour-toolbar">
      <div class="dash-segment" role="group" aria-label="Filtrer">
        <button
          v-for="opt in filterOptions"
          :key="opt.key"
          type="button"
          class="dash-segment-btn"
          :class="{ active: filter === opt.key }"
          :aria-pressed="filter === opt.key"
          @click="filter = opt.key"
        >
          {{ opt.label }} <span class="tour-count">{{ opt.count }}</span>
        </button>
      </div>
      <div class="tour-toolbar-right">
        <label class="tour-search">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input v-model="search" type="search" class="form-control" placeholder="Code, client, téléphone, adresse…" />
        </label>
        <div class="dash-segment" role="group" aria-label="Affichage">
          <button type="button" class="dash-segment-btn" :class="{ active: view === 'list' }" @click="view = 'list'">
            <i class="ph ph-list-numbers" aria-hidden="true"></i> Liste
          </button>
          <button type="button" class="dash-segment-btn" :class="{ active: view === 'map' }" @click="showMap">
            <i class="ph ph-map-trifold" aria-hidden="true"></i> Carte
          </button>
        </div>
      </div>
    </div>

    <AppLoader v-if="isLoader" variant="skeleton" :rows="5" />

    <div v-else-if="loadError" class="dash-empty">
      <i class="ph ph-cloud-slash" aria-hidden="true"></i>
      <p>Impossible de charger les colis de ce bon.</p>
      <button type="button" class="btn btn-primary" @click="load">Réessayer</button>
    </div>

    <template v-else>
      <!-- ── Map ──────────────────────────────────────────────────── -->
      <div v-show="view === 'map'" class="tour-map-wrap">
        <div ref="mapEl" class="tour-map"></div>
        <p class="tour-map-hint">Positions approximatives (centre du quartier).</p>
      </div>

      <!-- ── Stops ────────────────────────────────────────────────── -->
      <div v-if="view === 'list' && !visibleStops.length" class="dash-empty">
        <i class="ph ph-package" aria-hidden="true"></i>
        <p>{{ parcels.length ? "Aucun colis ne correspond." : "Aucun colis dans ce bon." }}</p>
      </div>

      <ol v-if="view === 'list'" class="tour-stops">
        <li
          v-for="stop in visibleStops"
          :key="stop.key"
          class="stop"
          :class="{ 'is-done': stop.allDone, 'is-next': stop.key === nextStopKey }"
        >
          <div class="stop-rail" aria-hidden="true">
            <span class="stop-num">
              <i v-if="stop.allDone" class="ph ph-check"></i>
              <template v-else>{{ stop.order }}</template>
            </span>
          </div>

          <div class="stop-body">
            <header class="stop-head">
              <div>
                <span v-if="stop.key === nextStopKey" class="stop-next">Prochain arrêt</span>
                <h2 class="stop-title">{{ stop.district || stop.city || "Adresse inconnue" }}</h2>
                <p class="stop-sub">
                  <template v-if="stop.district && stop.city">{{ stop.city }} · </template>
                  {{ stop.parcels.length }} colis
                  <template v-if="stop.cash"> · {{ money(stop.cash) }} {{ $devis }}</template>
                </p>
              </div>
              <span v-if="stop.legKm != null" class="dash-chip stop-dist" :title="stop.order === 1 ? 'Depuis votre position' : 'Depuis l\'arrêt précédent'">
                <i class="ph ph-path" aria-hidden="true"></i>
                {{ formatKm(stop.legKm) }}
              </span>
              <span v-else-if="!stop.coords" class="dash-chip stop-dist is-muted">Non localisé</span>
            </header>

            <article
              v-for="parcel in stop.parcels"
              :key="parcel.id"
              class="parcel"
              :class="{ 'is-done': isDone(parcel) }"
            >
              <div class="parcel-main">
                <div class="parcel-line">
                  <b class="parcel-code">{{ parcel.code }}</b>
                  <span class="parcel-status" :style="{ '--st': statusOf(parcel).color }">{{ statusOf(parcel).name }}</span>
                  <i v-if="parcel.replace == 1" class="ph ph-arrows-clockwise parcel-flag" title="Échange"></i>
                  <i v-if="parcel.fragile == 1" class="ph ph-wine parcel-flag" title="Fragile"></i>
                </div>
                <div class="parcel-receiver">{{ parcel.receiver || "—" }}</div>
                <div class="parcel-address">
                  <i class="ph ph-map-pin" aria-hidden="true"></i>
                  {{ parcel.address || "Adresse non renseignée" }}
                </div>
                <div v-if="parcel.note" class="parcel-note">
                  <i class="ph ph-chat-text" aria-hidden="true"></i> {{ parcel.note }}
                </div>
              </div>

              <div class="parcel-side">
                <div class="parcel-price">{{ money(parcel.price) }} <small>{{ $devis }}</small></div>
                <div class="parcel-actions">
                  <a
                    v-if="parcel.phone"
                    class="parcel-btn"
                    :href="`tel:${parcel.phone}`"
                    :aria-label="`Appeler ${parcel.receiver || ''}`"
                    title="Appeler"
                  >
                    <i class="ph ph-phone" aria-hidden="true"></i>
                  </a>
                  <a
                    class="parcel-btn is-primary"
                    :href="directionsUrl(parcel)"
                    target="_blank"
                    rel="noopener"
                    aria-label="Itinéraire"
                    title="Itinéraire"
                  >
                    <i class="ph ph-navigation-arrow" aria-hidden="true"></i>
                  </a>
                </div>
              </div>
            </article>
          </div>
        </li>
      </ol>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import moment from "moment";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/utils/axios";
import { useGlobalStore } from "@/stores/global";

type Coords = { lat: number; lng: number };

// Parcels have no GPS coordinates in the DB: each "stop" (quartier + ville) is
// geocoded once with OpenStreetMap Nominatim and cached in localStorage. Only the
// quartier / city names are sent — never the customer's name, phone or street.
const GEO_CACHE_PREFIX = "tour-geo:v1:";
const GEO_COUNTRY = process.env.VUE_APP_GEO_COUNTRY || "ma";
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const NOMINATIM_DELAY_MS = 1100; // Nominatim usage policy: max 1 request / second

const DONE_STATUSES = ["DELIVERED", "REFUSE", "RETURNED", "CANCELED", "CANCELLED"];

function readCache(query: string): Coords | null | undefined {
  try {
    const raw = localStorage.getItem(GEO_CACHE_PREFIX + query);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}
function writeCache(query: string, value: Coords | null) {
  try {
    localStorage.setItem(GEO_CACHE_PREFIX + query, JSON.stringify(value));
  } catch {
    // storage full / blocked: we simply geocode again next time
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function haversineKm(a: Coords, b: Coords) {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

const norm = (s: any) => String(s ?? "").trim();

export default defineComponent({
  name: "DistributionTour",
  setup() {
    const route = useRoute();
    const globalStore = useGlobalStore();
    const refNote = String(route.params.refNote || "");

    const note = ref<any>({});
    const parcels = ref<any[]>([]);
    const isLoader = ref(true);
    const loadError = ref(false);

    const filter = ref<"todo" | "done" | "all">("todo");
    const search = ref("");
    const view = ref<"list" | "map">("list");

    // ── Data ──
    async function load() {
      isLoader.value = true;
      loadError.value = false;
      try {
        const params = { ref: refNote };
        const [noteRes, savedRes, addedRes] = await Promise.all([
          api.get(`/api/auth/show_bon_distr`, { params }),
          api.get(`/api/auth/ListColisSavedDistr`, { params }),
          api.get(`/api/auth/ListColiNoSaveDist`, { params }),
        ]);
        note.value = noteRes.data || {};
        const byId = new Map<number, any>();
        [...(savedRes.data || []), ...(addedRes.data || [])].forEach((p: any) => byId.set(p.id, p));
        parcels.value = [...byId.values()];
      } catch (error) {
        console.error(error);
        loadError.value = true;
      } finally {
        isLoader.value = false;
      }
    }

    // ── Status helpers ──
    const isDone = (p: any) => DONE_STATUSES.includes(p.status) || DONE_STATUSES.includes(p.status_second);
    function statusOf(p: any) {
      const code = p.status === "IN_PROGRESS" && p.status_second ? p.status_second : p.status;
      const s: any = globalStore.globalHelper(code);
      return { name: s?.name || p.s_name || code || "—", color: s?.code_color || p.code_color || "#A8A29E" };
    }

    // ── Driver position ──
    const position = ref<Coords | null>(null);
    const geoState = ref<"pending" | "ok" | "denied" | "unavailable">("pending");
    function locate() {
      if (!("geolocation" in navigator)) {
        geoState.value = "unavailable";
        return;
      }
      geoState.value = "pending";
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          position.value = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          geoState.value = "ok";
        },
        (err) => {
          geoState.value = err.code === err.PERMISSION_DENIED ? "denied" : "unavailable";
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    }

    // ── Stops = parcels grouped by quartier + ville ──
    const stopKey = (p: any) => `${norm(p.district).toLowerCase()}|${norm(p.city_name).toLowerCase()}`;
    const coordsByKey = ref<Record<string, Coords | null>>({});
    const geocoding = ref(false);
    const geoDone = ref(0);
    const geoTotal = ref(0);

    async function nominatim(query: string): Promise<Coords | null> {
      const cached = readCache(query);
      if (cached !== undefined) return cached;
      const params = new URLSearchParams({ q: query, format: "json", limit: "1", countrycodes: GEO_COUNTRY });
      const res = await fetch(`${NOMINATIM_URL}?${params}`, { headers: { "Accept-Language": "fr" } });
      if (!res.ok) throw new Error(`Nominatim ${res.status}`);
      const data = await res.json();
      const value = data?.[0] ? { lat: Number(data[0].lat), lng: Number(data[0].lon) } : null;
      writeCache(query, value);
      await sleep(NOMINATIM_DELAY_MS);
      return value;
    }

    let geocodeRun = 0;
    async function geocodeStops() {
      const run = ++geocodeRun;
      const pending = new Map<string, any>();
      parcels.value.forEach((p) => {
        const key = stopKey(p);
        if (!(key in coordsByKey.value) && !pending.has(key)) pending.set(key, p);
      });
      if (!pending.size) return;
      geocoding.value = true;
      geoDone.value = 0;
      geoTotal.value = pending.size;
      for (const [key, p] of pending) {
        if (run !== geocodeRun) return;
        const city = norm(p.city_name);
        const district = norm(p.district);
        let coords: Coords | null = null;
        try {
          if (district && city) coords = await nominatim(`${district}, ${city}`);
          if (!coords && city) coords = await nominatim(city);
        } catch (error) {
          console.warn("Géolocalisation impossible :", error);
        }
        coordsByKey.value = { ...coordsByKey.value, [key]: coords };
        geoDone.value++;
      }
      geocoding.value = false;
    }

    // Greedy nearest-neighbour route: from the driver, always go to the closest
    // remaining stop. Stops without coordinates come last, by city then quartier.
    const stops = computed(() => {
      const groups = new Map<string, any>();
      parcels.value.forEach((p) => {
        const key = stopKey(p);
        if (!groups.has(key)) {
          groups.set(key, { key, city: norm(p.city_name), district: norm(p.district), parcels: [] as any[] });
        }
        groups.get(key).parcels.push(p);
      });

      const all = [...groups.values()].map((g) => {
        const todo = g.parcels.filter((p: any) => !isDone(p));
        g.parcels.sort((a: any, b: any) => Number(isDone(a)) - Number(isDone(b)) || norm(a.address).localeCompare(norm(b.address)));
        return {
          ...g,
          coords: coordsByKey.value[g.key] || null,
          allDone: todo.length === 0,
          cash: todo.reduce((sum: number, p: any) => sum + num(p.price), 0),
        };
      });

      const byName = (a: any, b: any) => a.city.localeCompare(b.city) || a.district.localeCompare(b.district);
      const located = all.filter((s) => s.coords && !s.allDone);
      const ordered: any[] = [];
      let from: Coords | null = position.value;
      if (!from && located.length) {
        located.sort(byName);
        from = located[0].coords;
      }
      while (located.length && from) {
        let best = 0;
        let bestKm = Infinity;
        located.forEach((s, i) => {
          const km = haversineKm(from as Coords, s.coords);
          if (km < bestKm) {
            bestKm = km;
            best = i;
          }
        });
        const [stop] = located.splice(best, 1);
        const isFirstWithoutGps = !position.value && !ordered.length;
        ordered.push({ ...stop, legKm: isFirstWithoutGps ? null : bestKm });
        from = stop.coords;
      }
      const rest = all
        .filter((s) => !s.allDone && !s.coords)
        .sort(byName)
        .map((s) => ({ ...s, legKm: null }));
      const done = all
        .filter((s) => s.allDone)
        .sort(byName)
        .map((s) => ({ ...s, legKm: null }));

      return [...ordered, ...rest, ...done].map((s, i) => ({ ...s, order: i + 1 }));
    });

    const unlocatedStops = computed(() => (geocoding.value ? 0 : stops.value.filter((s) => !s.coords && !s.allDone).length));
    const nextStopKey = computed(() => stops.value.find((s) => !s.allDone)?.key);

    // ── Filters ──
    const doneCount = computed(() => parcels.value.filter(isDone).length);
    const todoCount = computed(() => parcels.value.length - doneCount.value);
    const todoCash = computed(() => parcels.value.filter((p) => !isDone(p)).reduce((sum, p) => sum + num(p.price), 0));
    const progressPct = computed(() => (parcels.value.length ? Math.round((doneCount.value / parcels.value.length) * 100) : 0));
    const filterOptions = computed(() => [
      { key: "todo", label: "À livrer", count: todoCount.value },
      { key: "done", label: "Traités", count: doneCount.value },
      { key: "all", label: "Tous", count: parcels.value.length },
    ]);

    const visibleStops = computed(() => {
      const q = search.value.trim().toLowerCase();
      return stops.value
        .map((s) => ({
          ...s,
          parcels: s.parcels.filter((p: any) => {
            if (filter.value === "todo" && isDone(p)) return false;
            if (filter.value === "done" && !isDone(p)) return false;
            if (!q) return true;
            return [p.code, p.receiver, p.phone, p.address, p.district, p.city_name, p.note]
              .some((v) => norm(v).toLowerCase().includes(q));
          }),
        }))
        .filter((s) => s.parcels.length);
    });

    // ── Map (plain Leaflet, numbered markers) ──
    const mapEl = ref<HTMLElement | null>(null);
    let map: any = null;
    let layer: any = null;

    function renderMap() {
      if (!mapEl.value || view.value !== "map") return;
      if (!map) {
        map = L.map(mapEl.value, { zoomControl: true });
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap",
        }).addTo(map);
        layer = L.layerGroup().addTo(map);
      }
      layer.clearLayers();
      const points: [number, number][] = [];
      if (position.value) {
        const p: [number, number] = [position.value.lat, position.value.lng];
        points.push(p);
        L.marker(p, { icon: L.divIcon({ className: "tour-pin-me", html: "<span></span>", iconSize: [22, 22] }) })
          .bindTooltip("Vous êtes ici")
          .addTo(layer);
      }
      const route: [number, number][] = position.value ? [[position.value.lat, position.value.lng]] : [];
      visibleStops.value.forEach((s) => {
        if (!s.coords) return;
        const p: [number, number] = [s.coords.lat, s.coords.lng];
        points.push(p);
        if (!s.allDone) route.push(p);
        L.marker(p, {
          icon: L.divIcon({
            className: `tour-pin${s.allDone ? " is-done" : ""}${s.key === nextStopKey.value ? " is-next" : ""}`,
            html: `<span>${s.allDone ? "✓" : s.order}</span>`,
            iconSize: [30, 30],
          }),
        })
          .bindTooltip(`${s.order}. ${s.district || s.city} — ${s.parcels.length} colis`)
          .addTo(layer);
      });
      if (route.length > 1) {
        L.polyline(route, { color: getComputedStyle(document.body).getPropertyValue("--ui-accent").trim() || "#FF5500", weight: 3, opacity: 0.7, dashArray: "6 8" }).addTo(layer);
      }
      map.invalidateSize();
      if (points.length) map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
      else map.setView([31.8, -7.1], 6);
    }

    async function showMap() {
      view.value = "map";
      await nextTick();
      renderMap();
    }
    watch([visibleStops, position], () => {
      if (view.value === "map") nextTick(renderMap);
    });

    // ── Formatting / links ──
    function num(v: any) {
      const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    }
    const money = (v: any) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(num(v));
    const formatKm = (km: number) => (km < 1 ? `${Math.max(50, Math.round(km * 20) * 50)} m` : `${km.toFixed(1).replace(".", ",")} km`);
    const getDate = (v: string) => moment(String(v)).format("DD/MM/YYYY HH:mm");
    function directionsUrl(p: any) {
      const destination = [p.address, p.district, p.city_name].map(norm).filter(Boolean).join(", ");
      const params = new URLSearchParams({ api: "1", destination, travelmode: "driving" });
      return `https://www.google.com/maps/dir/?${params}`;
    }

    onMounted(async () => {
      locate();
      await load();
      geocodeStops();
    });
    onUnmounted(() => {
      geocodeRun++; // stop any geocoding loop
      if (map) map.remove();
    });

    return {
      refNote,
      note,
      parcels,
      isLoader,
      loadError,
      load,
      filter,
      filterOptions,
      search,
      view,
      showMap,
      mapEl,
      geoState,
      locate,
      geocoding,
      geoDone,
      geoTotal,
      unlocatedStops,
      visibleStops,
      nextStopKey,
      doneCount,
      todoCount,
      todoCash,
      progressPct,
      isDone,
      statusOf,
      money,
      formatKm,
      getDate,
      directionsUrl,
    };
  },
});
</script>

<style scoped>
.tour {
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-5);
  padding-bottom: var(--ui-space-6);
}

/* ── Header ─────────────────────────────────────────────────────── */
.tour-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--ui-space-4);
  flex-wrap: wrap;
  padding-bottom: var(--ui-space-5);
  border-bottom: 1px solid var(--ui-border);
}
.tour-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: var(--ui-space-3);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  text-decoration: none;
}
.tour-back:hover {
  color: var(--ui-accent-text);
}
.tour-title {
  margin: 0;
  color: var(--ui-text);
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.tour-sub {
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.tour-progress {
  min-width: 240px;
  padding: var(--ui-space-4) var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.tour-progress-top {
  display: flex;
  align-items: baseline;
  gap: 6px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.tour-progress-top b {
  color: var(--ui-text);
  font-size: 1.6rem;
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.tour-progress-bar {
  height: 8px;
  margin: 8px 0;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.tour-progress-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--ui-success);
  transition: width 800ms var(--ui-ease-spring);
}
.tour-progress small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}

/* ── Location banner ────────────────────────────────────────────── */
.tour-locate {
  --tone: var(--ui-warning-strong);
  --tone-soft: var(--ui-warning-soft);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: linear-gradient(90deg, var(--tone-soft), transparent 55%), var(--ui-surface);
}
.tour-locate.is-ok {
  --tone: var(--ui-success);
  --tone-soft: var(--ui-success-soft);
}
.tour-locate.is-pending {
  --tone: var(--ui-info);
  --tone-soft: var(--ui-info-soft);
}
.tour-locate-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 20px;
}
.tour-locate.is-pending .tour-locate-icon i {
  animation: tour-pulse 1.2s ease-in-out infinite;
}
.tour-locate-text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
}
.tour-locate-text span {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.tour-locate-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}
.tour-geo-bar {
  height: 3px;
  margin-top: calc(var(--ui-space-4) * -1);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.tour-geo-bar span {
  display: block;
  height: 100%;
  background: var(--ui-info);
  transition: width var(--ui-dur) var(--ui-ease);
}
@keyframes tour-pulse {
  50% { opacity: 0.35; }
}

/* ── Toolbar ────────────────────────────────────────────────────── */
.tour-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-3);
  flex-wrap: wrap;
}
.tour-toolbar-right {
  display: flex;
  align-items: center;
  gap: var(--ui-space-3);
  flex-wrap: wrap;
}
.dash-segment {
  display: flex;
  gap: 4px;
  max-width: 100%;
  padding: 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow-x: auto;
  scrollbar-width: none;
}
.dash-segment-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 8px 14px;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.dash-segment-btn:hover:not(.active) {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.dash-segment-btn.active {
  background: var(--ui-accent);
  color: #FFFFFF;
}
.tour-count {
  min-width: 22px;
  padding: 1px 7px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-micro);
  text-align: center;
}
.dash-segment-btn.active .tour-count {
  background: rgba(255, 255, 255, 0.25);
  color: #FFFFFF;
}
.tour-search {
  position: relative;
  margin: 0;
}
.tour-search i {
  position: absolute;
  top: 50%;
  left: 12px;
  color: var(--ui-text-muted);
  transform: translateY(-50%);
}
.tour-search .form-control {
  min-width: 260px;
  padding-left: 36px;
}

/* ── Stops timeline ─────────────────────────────────────────────── */
.tour-stops {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}
.stop {
  display: flex;
  gap: var(--ui-space-4);
  animation: tour-rise 420ms var(--ui-ease-spring) both;
}
.stop-rail {
  position: relative;
  display: flex;
  flex: 0 0 40px;
  justify-content: center;
}
.stop-rail::after {
  content: "";
  position: absolute;
  top: 44px;
  bottom: 0;
  left: 50%;
  border-left: 2px dashed var(--ui-border-strong);
}
.stop:last-child .stop-rail::after {
  display: none;
}
.stop-num {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 2px solid var(--ui-accent);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.stop.is-next .stop-num {
  background: var(--ui-accent);
  color: #FFFFFF;
  box-shadow: 0 0 0 6px rgba(var(--ui-accent-rgb), 0.18);
}
.stop.is-done .stop-num {
  border-color: var(--ui-success);
  background: var(--ui-success);
  color: #FFFFFF;
}
.stop-body {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: var(--ui-space-5);
  padding: var(--ui-space-4) var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.stop.is-next .stop-body {
  border-color: var(--ui-accent);
}
.stop.is-done .stop-body {
  opacity: 0.7;
}
.stop-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ui-space-3);
  margin-bottom: var(--ui-space-3);
}
.stop-next {
  display: inline-block;
  margin-bottom: 2px;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.stop-title {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.stop-sub {
  margin: 2px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.stop-dist {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.stop-dist.is-muted {
  opacity: 0.7;
}

/* ── Parcel rows ────────────────────────────────────────────────── */
.parcel {
  display: flex;
  justify-content: space-between;
  gap: var(--ui-space-4);
  padding: var(--ui-space-3) 0;
  border-top: 1px solid var(--ui-border);
}
.parcel.is-done .parcel-main {
  opacity: 0.6;
}
.parcel-main {
  min-width: 0;
}
.parcel-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.parcel-code {
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
  letter-spacing: 0.02em;
}
.parcel-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.parcel-status::before {
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--st);
}
.parcel-flag {
  color: var(--ui-warning-strong);
}
.parcel-receiver {
  margin-top: 4px;
  color: var(--ui-text);
  font-weight: var(--ui-fw-bold);
}
.parcel-address,
.parcel-note {
  display: flex;
  gap: 6px;
  margin-top: 2px;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  overflow-wrap: anywhere;
}
.parcel-address i,
.parcel-note i {
  flex: 0 0 auto;
  margin-top: 3px;
  color: var(--ui-text-muted);
}
.parcel-note {
  color: var(--ui-text-muted);
  font-style: italic;
}
.parcel-side {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}
.parcel-price {
  color: var(--ui-text);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.parcel-price small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.parcel-actions {
  display: flex;
  gap: 8px;
}
.parcel-btn {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-success);
  font-size: 19px;
  text-decoration: none;
  transition: transform var(--ui-dur-fast) var(--ui-ease), background-color var(--ui-dur-fast) var(--ui-ease);
}
.parcel-btn:hover {
  transform: translateY(-1px);
  background: var(--ui-success-soft);
}
.parcel-btn.is-primary {
  border-color: var(--ui-accent);
  background: var(--ui-accent);
  color: #FFFFFF;
}
.parcel-btn.is-primary:hover {
  background: var(--ui-accent-strong);
}

/* ── Map ────────────────────────────────────────────────────────── */
.tour-map-wrap {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow: hidden;
}
.tour-map {
  height: min(65vh, 560px);
  z-index: 0;
}
.tour-map-hint {
  margin: 0;
  padding: 8px 14px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.tour-map :deep(.tour-pin) {
  display: grid;
  place-items: center;
  border: 2px solid #FFFFFF;
  border-radius: 50%;
  background: var(--ui-accent);
  color: var(--ui-on-accent);
  font: 800 13px/1 Inter, sans-serif;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
}
.tour-map :deep(.tour-pin.is-next) {
  box-shadow: 0 0 0 6px rgba(var(--ui-accent-rgb), 0.3), 0 3px 10px rgba(0, 0, 0, 0.3);
}
.tour-map :deep(.tour-pin.is-done) {
  background: #16A34A;
}
.tour-map :deep(.tour-pin-me span) {
  display: block;
  width: 22px;
  height: 22px;
  border: 3px solid #FFFFFF;
  border-radius: 50%;
  background: #2563EB;
  box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.25);
}

@keyframes tour-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
}

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 767.98px) {
  .tour-progress { width: 100%; }
  .tour-toolbar, .tour-toolbar-right { width: 100%; }
  .tour-search { flex: 1 1 100%; }
  .tour-search .form-control { min-width: 0; width: 100%; }
  .tour-locate { flex-wrap: wrap; }
  .tour-locate-btn { width: 100%; justify-content: center; }
  .stop { gap: var(--ui-space-3); }
  .stop-rail { flex-basis: 32px; }
  .stop-num { width: 32px; height: 32px; font-size: var(--ui-fs-sm); }
  .stop-rail::after { top: 36px; }
  .stop-body { padding: var(--ui-space-3) var(--ui-space-4); }
  .parcel { flex-direction: column; gap: var(--ui-space-2); }
  .parcel-side { flex-direction: row; align-items: center; }
}
@media (prefers-reduced-motion: reduce) {
  .stop { animation: none; }
  .tour-locate.is-pending .tour-locate-icon i { animation: none; }
}
</style>
